import React, { useEffect, useRef } from 'react';
import { useSession } from '@baltlife/internal-sso-react';
import { sso } from '../../../auth/sso';

/**
 * Internal-SSO route guard (replaces the MSAL useMsalAuthentication dance).
 *
 * Mounted as part of the pathless layout route in App.jsx, so it is an ancestor of the
 * authenticated routes ONLY. The routes that must run before a session exists —
 * /oauth/callback, /Error/* — are siblings of that layout route and never reach this
 * component, which is why it no longer has to ask what path it is on.
 *
 * - DEV bypass (sso.isDev, opt-in via REACT_APP_SSO_DEV_BYPASS): render children without
 *   a Stytch session. UserProvider still gates on claims it cannot get without a token,
 *   so this path renders blank — it is off unless explicitly enabled.
 * - Live session present: render children.
 * - Otherwise: ask the package's guard where to send the browser, and go there.
 *
 * The destination is the guard's to choose, not this component's. A refusal has two
 * causes and they need different targets: no session means mint a login transaction, but
 * a sign-out already in flight means go to the LOGOUT url — minting a login there would
 * supersede the sign-out redirect and hand the user straight back a session. The guard
 * also owns the sign-out latch, including the fact that reading it CONSUMES it, so this
 * component must not read it too: a second read eats the latch the guard needs, and the
 * bounce-back from the auth site then renders a sessionless app instead of redirecting.
 */
export function UserAuthentication({ children }) {
  const { isAuthenticated } = useSession();
  const redirecting = useRef(false);
  const allowed = sso.isDev || isAuthenticated;

  useEffect(() => {
    if (allowed || redirecting.current) {
      return;
    }
    redirecting.current = true;
    // destination = the full deep-link the user asked for; restored after login via the
    // OIDC state round-trip.
    sso
      .checkAuthenticated(window.location.href)
      .then((result) => {
        if (result.authenticated) {
          // The store holds a session this render had not seen yet — nowhere to send
          // anyone. Re-arm so a later genuine refusal is not swallowed by the ref.
          redirecting.current = false;
          return;
        }
        window.location.assign(result.redirectUrl);
      })
      .catch(() => {
        // If no destination can be built (misconfig), fall back to the app root rather
        // than a blank page; the auth site surfaces the real error.
        window.location.assign('/');
      });
  }, [allowed]);

  if (!allowed) {
    return null;
  }

  return <>{children}</>;
}
